/**
 * Authentication utility functions for handling user authentication state and tokens
 */
export const authUtils = {
  /**
   * Checks if a user is currently authenticated by verifying the presence of an auth token
   * @returns {boolean} True if a valid auth token exists, false otherwise
   */
  isAuthenticated: (): boolean => {
    try {
      const token = localStorage.getItem('auth_token');
      return !!token; // Return true if token exists
    } catch (error) {
      console.error('Error checking authentication:', error);
      return false;
    }
  },

  /**
   * Retrieves the authentication token from localStorage
   * @returns {string | null} The authentication token if it exists, null otherwise
   */
  getToken: (): string | null => {
    try {
      return localStorage.getItem('auth_token');
    } catch (error) {
      console.error('Error getting token:', error);
      return null;
    }
  },

  /**
   * Stores the authentication token in localStorage
   * @param {string} token - The authentication token to store
   * @returns {void}
   */
  setToken: (token: string): void => {
    try {
      localStorage.setItem('auth_token', token);
    } catch (error) {
      console.error('Error setting token:', error);
    }
  },

  /**
   * Removes the authentication token from localStorage
   * @returns {void}
   */
  removeToken: (): void => {
    try {
      localStorage.removeItem('auth_token');
    } catch (error) {
      console.error('Error removing token:', error);
    }
  }
};

/**
 * Extracts clean text content from HTML by removing all HTML tags, comments, and scripts
 * @param {string} html - The HTML string to process
 * @returns {string} The cleaned text content with normalized whitespace
 * @throws {Error} If there's an error during HTML parsing
 * @example
 * const text = extractTextFromHtml('<div>Hello <b>World</b>!</div>');
 * // Returns: 'Hello World!'
 */
export const extractTextFromHtml = (html: string): string => {
  if (!html || typeof html !== 'string') return '';

  try {
    // Remove HTML comments first
    let cleanHtml = html.replace(/<!--[\s\S]*?-->/g, '');

    // Remove style tags and their content
    cleanHtml = cleanHtml.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');

    // Remove script tags and their content
    cleanHtml = cleanHtml.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');

    // Create a temporary DOM element to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = cleanHtml;

    // Extract text content, removing HTML tags
    const textContent = tempDiv.textContent || tempDiv.innerText || '';

    // Clean up extra whitespace and line breaks
    return textContent
      .replace(/\s+/g, ' ') // Replace multiple whitespace with single space
      .trim();
  } catch (error) {
    console.error('Error extracting text from HTML:', error);
    // Fallback: try to extract text using regex if DOM parsing fails
    try {
      // Use a safer approach for HTML comment removal
      const doc = new DOMParser().parseFromString(html, 'text/html');
      
      // Remove script and style elements
      const scripts = doc.querySelectorAll('script, style');
      scripts.forEach(script => script.remove());
      
      // Get text content and clean up whitespace
      return doc.body.textContent
        ? doc.body.textContent
            .replace(/\s+/g, ' ') // Replace all whitespace sequences with a single space
            .trim()
        : '';
    } catch (regexError) {
      console.error('Error with regex fallback:', regexError);
      return html; // Return original if extraction fails
    }
  }
};
